<template>
<div style="display: flex; width: 100%;">
	<div style="flex-basis: 100%">
		<el-select
			v-model="modelValue"
			:disabled="disabled"
			:allow-create="allowCreating"
			:default-first-option="allowCreating"
			filterable
			remote
			reserve-keyword
			clearable
			:multiple="multiple"
			:placeholder="placeholder"
			:remote-method="searchAsync"
			:loading="selectLoadingModel"
			:remote-show-suffix="showSuffix"
			:suffix-icon="suffixIcon"
			:size="size"
			@change="onSelectChange"
		>
			<template #empty>
				<slot name="empty">
					Ничего не найдено ☹️
				</slot>				
			</template>
			<template #label="item">
				<slot v-bind="item" name="label">
					default label template
					<div>{{ item }}</div>
				</slot>
			</template>
			<el-option
				v-for="item in selectItems"
				:key="item.id"
				:value="item.id">
				<slot v-bind="item">
					default template
					<div>{{ item }}</div>
				</slot>
			</el-option>
		</el-select>
	</div>
	<div v-if="catalogTab?.view" class="catalog-button-wrapper">
		<el-icon @click="onPickFromCatalogClick" :style="{ margin: (size == 'small' ? '2px 4px 0' : '6px 8px 0 8px') }" size="large"><Memo /></el-icon>
	</div>
</div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Memo } from '@element-plus/icons-vue';
import store from '@/store';
import ApplicationTab from '@/models/ApplicationTab';
import PickerConfigurationModel from '@/models/PickerConfigurationModel';
import axiosHelper from '@/helpers/axiosHelper';
import { AxiosInstance } from 'axios';

@Options({	
	components: {
		Memo
	},
	props: {
		disabled: Boolean,
		auth: Boolean,
		searchUrl: String,
		catalogTab: ApplicationTab,
		searchResultsMap: Function,		
		placeholder: String,
		allowCreating: Boolean,
		multiple: Boolean,
		size: String,
		showSuffix: {
			type: Boolean,
			default: false
		},
		suffixIcon: {
			type: Object,
			default: null
		},
		searchParameters: {
			type: Object,
			default: null
		}
	},
	emits: ['update:modelValue']
})
export default class CatalogPickerComponent extends Vue {
	/** props */
	disabled!: boolean;
	auth!: boolean;
	searchUrl!: string;
	placeholder!: string;
	searchResultsMap!: Function;	
	catalogTab!: ApplicationTab;
	allowCreating!: boolean;
	multiple!: boolean;
	showSuffix!: boolean;
	suffixIcon!: any;
	searchParameters!: any;
	size!: string;

	/** constants */
	httpSearchUrl: AxiosInstance | null = null;

	/** view-models */
	selectItems: any[] | null = null;
	modelValue: any;
	selectLoadingModel = false;

	mounted() {
		if (this.auth) {
			const jwt = store.getters.jwt;
			this.httpSearchUrl = axiosHelper.createInstance(jwt);
		} else {
			this.httpSearchUrl = axiosHelper.createInstance();
		}
	}

	onSelectChange(selectedItemId: any) {
		if (this.allowCreating) {
			if (!this.selectItems?.length) {				
				this.$emit('update:modelValue', selectedItemId);
			} else {
				const item = this.selectItems.find(_ => _.id == selectedItemId);
				this.$emit('update:modelValue', item);
			}			
		} else {
			if (!this.selectItems?.length) {			
				this.$emit('update:modelValue', null);
				return;
			}

			if (this.multiple) { 						
				const selectedItems = this.selectItems.filter((_: any) => this.modelValue?.find((v: any) => v == _.id));					
				this.$emit('update:modelValue', selectedItems);
			} else {
				const item = this.selectItems.find(_ => _.id == selectedItemId);
				this.$emit('update:modelValue', item);
			}
		}
	}

	async searchAsync(query: string) {
		if (!this.searchUrl) {
			console.error('CatalogPickerComponent: no searching url was provided. Pass URL with attribute \'search-url\'')
			return;
		}

		if (!query.length) {
			return;
		}

		let responseData: Array<any>;
		let params: any = { query };
		
		if (this.searchParameters) {
			params = { 
				...params,
				...Object.fromEntries(
					// eslint-disable-next-line
					Object.entries(this.searchParameters).filter(([_, value]) => value !== undefined)
				)
			};
		}

		try {
			const response = await this.httpSearchUrl!.get(this.searchUrl, { params });
			if (!response.data?.length) {
				return [];
			}
			responseData = response.data;
		} catch (e) {			
			console.error('CatalogPickerComponent: error searching for entities', e);
			return;
		}

		if (typeof this.searchResultsMap === 'function') {
			const mappedResults = this.searchResultsMap(responseData);
			this.selectItems = mappedResults;
		} else {
			this.selectItems = responseData;
		}
	}

	onPickFromCatalogClick() {
		if (!this.catalogTab?.view?.length) {
			return;
		}

		const pickerConfig = new PickerConfigurationModel({ 
			isPickerModeEnabled: true,
			onItemPickedCallback: (payload: any) => {
				this.$emit('catalog-item-picked', payload);
				store.commit('removeTab', store.getters.tab);
			}
		});

		this.catalogTab.params = [ pickerConfig ];
		this.catalogTab.allowMultiple = true;
		store.commit('addTab', this.catalogTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);		
	}

	set(item: any) {
		if (!item) {
			this.selectItems = null;
			this.modelValue = null;
		} else {
			if (Array.isArray(item)) {
				if (item?.length) {
					this.selectItems = item;
					this.modelValue = item.map((_: any) => _.id);
				}
			} else {
				const itemId = item.id;
				this.modelValue = itemId;
				this.selectItems = [item];				
				this.onSelectChange(itemId);
			}
		}		
	}

	setItems(items?: Array<any>) {
		this.selectItems = items ?? null;
	}
}
</script>

<style scoped>
.catalog-button-wrapper {
	margin-top: 1px;	
	height: 30px; 
	cursor: pointer; 
	position: relative;
	z-index: 99;
}

	.catalog-button-wrapper svg {
		color: var(--el-color-info-dark-2);
		transition: color ease-in-out .15s;
	}

.catalog-button-wrapper:hover {
	
}

	.catalog-button-wrapper:hover svg {
		color: var(--el-color-primary);
	}
</style>